import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { recordLaunchEvent, submitBrandRepInterest, subscribeToLaunchNotify } from '../../util/api';
import {
  DEFAULT_AI_DISCLOSURE,
  LAUNCH_EVENT_ENQUIRY,
  LAUNCH_EVENT_NOTIFY_SIGNUP,
  LAUNCH_EVENT_OUTBOUND_CLICK,
  LAUNCH_EVENT_SAMPLE_REQUEST,
  LAUNCH_EVENT_SHARE,
  LAUNCH_IMAGE_SOURCE_AI,
  LAUNCH_LISTING_TYPE,
  LAUNCH_REGION_OPTIONS,
  LAUNCH_STATUS_ARCHIVED,
  LAUNCH_STATUS_COMING_SOON,
  toEpochDay,
} from '../../config/configWhatsNew';

import { NamedLink, ResponsiveImage } from '../../components';
import SaveButton from '../../components/SaveButton/SaveButton';
import ShareButton from '../../components/ShareButton/ShareButton';
import LaunchSampleModal from '../../components/LaunchSampleModal/LaunchSampleModal';
import LaunchCard, {
  LAUNCH_TYPE_LABELS,
  formatLaunchDate,
} from '../../components/LaunchCard/LaunchCard';
import BrandRepModal from '../../components/BrandRepModal/BrandRepModal';

import SectionTextMaybe from './SectionTextMaybe';
import css from './SectionLaunchLayout.module.css';

const REGION_LABELS = LAUNCH_REGION_OPTIONS.reduce(
  (acc, o) => ({ ...acc, [o.option]: o.label }),
  {}
);

const asArray = v => (Array.isArray(v) ? v : v ? [v] : []);
const trimmed = v => (typeof v === 'string' && v.trim() ? v.trim() : null);

// Whole days between now and the launch date, floor'd at 0. Day granularity is
// deliberate: an hours-and-minutes countdown on a date the brand set in a
// press release would imply a precision nobody has.
const daysUntil = (epochDay, now = Date.now()) => {
  const n = Number(epochDay);
  if (epochDay == null || !Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n) - toEpochDay(now));
};

// ── Notify me ────────────────────────────────────────────────────────────────
//
// Signed-in visitors are identified from the session and need type nothing;
// signed-out visitors give an address, and giving it IS the consent the server
// stamps. Duplicate-safe by design: the endpoint answers "you're on the list"
// either way, and so do we — "you already subscribed" is the truthful version
// of the same good news, not an error.
const NotifyMe = ({ launchId, isSignedIn }) => {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');

  const submit = e => {
    e.preventDefault();
    if (state === 'sending') return;
    const address = email.trim();
    if (!isSignedIn && !address) return;

    setState('sending');
    subscribeToLaunchNotify({ launchId, ...(address ? { email: address } : {}) })
      .then(res => {
        if (!res || res.stored !== true) {
          setState('failed');
          return;
        }
        setState(res.duplicate ? 'duplicate' : 'done');
        recordLaunchEvent({ launchId, event: LAUNCH_EVENT_NOTIFY_SIGNUP }).catch(() => {});
      })
      .catch(() => setState('failed'));
  };

  if (state === 'done' || state === 'duplicate') {
    return (
      <p className={css.notifyResult}>
        <FormattedMessage
          id={state === 'duplicate' ? 'LaunchPage.notifyAlready' : 'LaunchPage.notifyThanks'}
        />
      </p>
    );
  }

  return (
    <form className={css.notifyForm} onSubmit={submit}>
      {!isSignedIn ? (
        <input
          className={css.notifyInput}
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={intl.formatMessage({ id: 'LaunchPage.notifyPlaceholder' })}
          aria-label={intl.formatMessage({ id: 'LaunchPage.notifyPlaceholder' })}
        />
      ) : null}
      <button className={css.notifyButton} type="submit" disabled={state === 'sending'}>
        <FormattedMessage id="LaunchPage.notifyCta" />
      </button>
      {state === 'failed' ? (
        <p className={css.notifyError}>
          <FormattedMessage id="LaunchPage.notifyFailed" />
        </p>
      ) : null}
    </form>
  );
};

/**
 * The editorial layout for a "What's New" launch.
 *
 * Lives in its own file rather than inline in ListingPageCarousel: the branch
 * decision, the SEO block and the page shell stay in the carousel (that is
 * where a reader looks for "what does this listing render as"), while ~350
 * lines of launch-specific markup sit next to the other Section* modules in
 * this folder. Same split the design-showcase layout would get if it were
 * written today.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 *   - It never invents a value. A missing tech highlight is a row that isn't
 *     rendered; a missing availability note is a line that isn't there. There
 *     is no "Not provided" placeholder anywhere in the launch schema, because
 *     a table of "Not provided" reads as a product with nothing to say rather
 *     than as an editor who hasn't verified it yet.
 *   - It never shows an ai_concept image without its disclosure caption.
 *   - Archived launches keep the whole page (URL and SEO survive archiving)
 *     but lose every action except Share: sampling something that has been
 *     withdrawn wastes a designer's afternoon.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing - the launch listing entity
 * @param {Object} props.currentUser
 * @param {boolean} props.isOwnListing
 * @param {Array} props.relatedProducts - catalog listings from relatedProductIds
 * @param {Array} props.relatedLaunches - sibling live launches
 * @param {Object} props.routeConfiguration
 * @param {Function} props.onManageDisableScrolling
 * @param {ReactNode} [props.actionBar] - the owner/admin action bar, if any
 */
const SectionLaunchLayout = props => {
  const {
    listing,
    currentUser,
    isOwnListing,
    relatedProducts = [],
    relatedLaunches = [],
    routeConfiguration,
    onManageDisableScrolling,
    actionBar,
  } = props;
  const intl = useIntl();

  const [sampleOpen, setSampleOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const { title = '', description = '', publicData = {} } = listing?.attributes || {};
  const pd = publicData;
  const launchId = listing?.id?.uuid;
  const images = listing?.images || [];

  const status = pd.launchStatus;
  const isComingSoon = status === LAUNCH_STATUS_COMING_SOON;
  const isArchived = status === LAUNCH_STATUS_ARCHIVED;

  const dateLabel = formatLaunchDate(pd.launchDate, intl);
  const typeLabel = LAUNCH_TYPE_LABELS[pd.launchType] || null;
  const countdown = isComingSoon ? daysUntil(pd.launchDate) : null;

  const heroImage = images[0] || null;
  const galleryImages = images.slice(1);
  const attributions =
    pd.imageAttributions && typeof pd.imageAttributions === 'object' ? pd.imageAttributions : {};
  const aiDisclosure = trimmed(pd.aiVisualDisclosure) || DEFAULT_AI_DISCLOSURE;
  const isAiImage = img => attributions[(img?.id?.uuid)] === LAUNCH_IMAGE_SOURCE_AI;

  // Verified facts only — a row with no value is not a row.
  const techRows = Array.isArray(pd.techHighlights)
    ? pd.techHighlights
        .map(r => ({ label: trimmed(r?.label), value: trimmed(r?.value) }))
        .filter(r => r.label && r.value)
    : [];

  const regions = asArray(pd.availabilityRegions)
    .map(r => REGION_LABELS[r] || r)
    .filter(Boolean);

  const supplyRows = [
    { key: 'brand', labelId: 'LaunchPage.factBrand', value: trimmed(pd.brandName) },
    { key: 'supplier', labelId: 'LaunchPage.factSupplier', value: trimmed(pd.supplierName) },
    { key: 'origin', labelId: 'LaunchPage.factOrigin', value: trimmed(pd.countryOfOrigin) },
    { key: 'regions', labelId: 'LaunchPage.factRegions', value: regions.join(', ') || null },
    { key: 'type', labelId: 'LaunchPage.factType', value: typeLabel },
    { key: 'date', labelId: 'LaunchPage.factDate', value: dateLabel },
  ].filter(r => r.value);

  const brandLabel = trimmed(pd.brandName) || trimmed(pd.supplierName);
  const sourceUrl = trimmed(pd.sourceUrl);
  const sourceLabel = trimmed(pd.sourceLabel);

  const fire = (event, metadata) => {
    if (!launchId) return;
    recordLaunchEvent({
      launchId,
      event,
      metadata: {
        ...(pd.launchType ? { launch_type: pd.launchType } : {}),
        ...(pd.brandSlug ? { brand_slug: pd.brandSlug } : {}),
        ...(metadata || {}),
      },
    }).catch(() => {});
  };

  // Actions by status. Archived keeps Share only; coming soon swaps Sample for
  // Notify (you cannot sample something that hasn't shipped), and Save stays
  // available throughout — saving IS how a designer marks "come back to this".
  const showSave = !isArchived && !isOwnListing;
  const showSample = !isArchived && !isComingSoon;
  const showNotify = isComingSoon;

  const shareButton = (
    <ShareButton
      className={css.actionShare}
      title={title}
      text={trimmed(pd.subtitle) || undefined}
      onShare={method => fire(LAUNCH_EVENT_SHARE, { source_section: method })}
    />
  );

  const sampleButton = showSample ? (
    <button type="button" className={css.actionPrimary} onClick={() => setSampleOpen(true)}>
      <FormattedMessage id="LaunchPage.requestSample" />
    </button>
  ) : null;

  const saveButton =
    showSave && launchId ? (
      <SaveButton
        listingId={launchId}
        size="page"
        listingType={LAUNCH_LISTING_TYPE}
        className={css.actionSave}
      />
    ) : null;

  return (
    <div className={css.launchPage}>
      {actionBar}

      {isArchived ? (
        <div className={css.archivedBanner} role="status">
          <strong className={css.archivedTitle}>
            <FormattedMessage id="LaunchPage.archivedTitle" />
          </strong>
          <span className={css.archivedBody}>
            <FormattedMessage id="LaunchPage.archivedBody" />
          </span>
        </div>
      ) : null}

      <article className={css.article}>
        {/* ── hero ─────────────────────────────────────────────────── */}
        <header className={css.hero}>
          <div className={css.statusRow}>
            <span
              className={classNames(css.statusPill, {
                [css.statusPillComingSoon]: isComingSoon,
                [css.statusPillArchived]: isArchived,
              })}
            >
              <FormattedMessage
                id={
                  isArchived
                    ? 'LaunchPage.statusArchived'
                    : isComingSoon
                    ? 'LaunchPage.statusComingSoon'
                    : 'LaunchPage.statusLive'
                }
              />
            </span>
            {typeLabel ? <span className={css.statusMeta}>{typeLabel}</span> : null}
            {dateLabel ? <span className={css.statusMeta}>{dateLabel}</span> : null}
          </div>

          {brandLabel ? (
            pd.brandSlug ? (
              // Straight into the brand's catalog — the natural next question
              // after "who makes this" is "what else do they make".
              <a
                className={css.brandLink}
                href={`/s?pub_brand_slug=${encodeURIComponent(pd.brandSlug)}`}
              >
                {brandLabel}
              </a>
            ) : (
              <span className={css.brandName}>{brandLabel}</span>
            )
          ) : null}

          <h1 className={css.title}>{title}</h1>
          {trimmed(pd.subtitle) ? <p className={css.subtitle}>{pd.subtitle}</p> : null}
        </header>

        {heroImage ? (
          <figure className={css.heroFigure}>
            <ResponsiveImage
              rootClassName={css.heroImage}
              alt={title}
              image={heroImage}
              variants={['scaled-xlarge', 'scaled-large', 'scaled-medium']}
              sizes="(max-width: 1023px) 100vw, 960px"
            />
            {isAiImage(heroImage) ? (
              <figcaption className={css.aiCaption}>{aiDisclosure}</figcaption>
            ) : null}
          </figure>
        ) : null}

        {/* ── coming soon: countdown + notify ──────────────────────── */}
        {isComingSoon ? (
          <section className={css.comingSoon} id="notify">
            <div className={css.countdown}>
              {countdown === 0 ? (
                <FormattedMessage id="LaunchPage.countdownToday" />
              ) : countdown != null ? (
                <FormattedMessage id="LaunchPage.countdownDays" values={{ days: countdown }} />
              ) : (
                <FormattedMessage id="LaunchPage.countdownUnknown" />
              )}
            </div>
            <p className={css.comingSoonBody}>
              <FormattedMessage id="LaunchPage.comingSoonBody" />
            </p>
            {launchId ? <NotifyMe launchId={launchId} isSignedIn={!!currentUser?.id} /> : null}
          </section>
        ) : null}

        {/* ── desktop action row ───────────────────────────────────── */}
        {/* The mobile bar at the foot of this component carries the same
            actions for a thumb; exactly one of the two is visible at any width
            (media queries, not conditional rendering, so there is no layout
            jump at the breakpoint). Neither copy holds state that could drift:
            SaveButton reads redux, and the modals are owned by THIS component,
            not by the buttons. Contrast the OrderPanel path on the standard
            layout, where ModalInMobile renders its children inline on mobile
            as well and a naively duplicated CTA appears twice. */}
        <div className={css.actionsRow}>
          {saveButton}
          {sampleButton}
          {shareButton}
        </div>

        {/* ── editorial ────────────────────────────────────────────── */}
        {trimmed(pd.editorialSummary) ? (
          <section className={css.section}>
            <SectionTextMaybe text={pd.editorialSummary} showAsIngress />
          </section>
        ) : null}

        {trimmed(pd.whyItMatters) ? (
          <section className={classNames(css.section, css.whyItMatters)}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="LaunchPage.whyItMattersTitle" />
            </h2>
            <SectionTextMaybe text={pd.whyItMatters} />
          </section>
        ) : null}

        {/* The listing's own description is the long-form body when the editor
            wrote one; the summary above is the short version. */}
        {trimmed(description) && description !== pd.editorialSummary ? (
          <section className={css.section}>
            <SectionTextMaybe text={description} />
          </section>
        ) : null}

        {/* ── gallery ──────────────────────────────────────────────── */}
        {galleryImages.length > 0 ? (
          <section className={css.gallery}>
            {galleryImages.map((img, i) => (
              <figure key={img.id?.uuid || i} className={css.galleryFigure}>
                <ResponsiveImage
                  rootClassName={css.galleryImage}
                  alt={`${title} — ${i + 2}`}
                  image={img}
                  variants={['scaled-large', 'scaled-medium']}
                  sizes="(max-width: 767px) 100vw, 480px"
                  loading="lazy"
                />
                {isAiImage(img) ? (
                  <figcaption className={css.aiCaption}>{aiDisclosure}</figcaption>
                ) : null}
              </figure>
            ))}
          </section>
        ) : null}

        {/* ── tech highlights ──────────────────────────────────────── */}
        {techRows.length > 0 ? (
          <section className={css.section}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="LaunchPage.techHighlightsTitle" />
            </h2>
            <ul className={css.factList}>
              {techRows.map(row => (
                <li key={row.label} className={css.factRow}>
                  <span className={css.factLabel}>{row.label}</span>
                  <span className={css.factValue}>{row.value}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* ── availability + supplier ──────────────────────────────── */}
        {supplyRows.length > 0 || trimmed(pd.availabilityNote) || sourceUrl ? (
          <section className={css.section}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="LaunchPage.availabilityTitle" />
            </h2>
            {trimmed(pd.availabilityNote) ? (
              <p className={css.availabilityNote}>{pd.availabilityNote}</p>
            ) : null}
            {supplyRows.length > 0 ? (
              <ul className={css.factList}>
                {supplyRows.map(row => (
                  <li key={row.key} className={css.factRow}>
                    <span className={css.factLabel}>
                      <FormattedMessage id={row.labelId} />
                    </span>
                    <span className={css.factValue}>{row.value}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className={css.supplierActions}>
              {!isArchived ? (
                <button
                  type="button"
                  className={css.actionSecondary}
                  onClick={() => setEnquiryOpen(true)}
                >
                  <FormattedMessage id="LaunchPage.contactSupplier" />
                </button>
              ) : null}
              {sourceUrl ? (
                <a
                  className={css.sourceLink}
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={() => fire(LAUNCH_EVENT_OUTBOUND_CLICK)}
                >
                  {sourceLabel || <FormattedMessage id="LaunchPage.sourceFallback" />}
                  <span aria-hidden="true"> ↗</span>
                </a>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* ── related catalog products ─────────────────────────────── */}
        {relatedProducts.length > 0 ? (
          <section className={css.section}>
            <h2 className={css.sectionTitle}>
              <FormattedMessage id="LaunchPage.relatedProductsTitle" />
            </h2>
            <div className={css.productRail}>
              {relatedProducts.map(p => (
                <a
                  key={p.id.uuid}
                  className={css.productCard}
                  href={pathByRouteName('ListingPage', routeConfiguration, {
                    id: p.id.uuid,
                    slug: createSlug(p.attributes?.title || 'listing'),
                  })}
                >
                  <span className={css.productImageWrap}>
                    <ResponsiveImage
                      rootClassName={css.productImage}
                      alt={p.attributes?.title || ''}
                      image={(p.images || [])[0] || null}
                      variants={['listing-card', 'listing-card-2x']}
                      sizes="140px"
                      loading="lazy"
                    />
                  </span>
                  <span className={css.productTitle}>{p.attributes?.title}</span>
                  {p.author?.attributes?.profile?.displayName ? (
                    <span className={css.productBrand}>
                      {p.author.attributes.profile.displayName}
                    </span>
                  ) : null}
                </a>
              ))}
            </div>
          </section>
        ) : null}

        {/* ── related launches ─────────────────────────────────────── */}
        {relatedLaunches.length > 0 ? (
          <section className={css.section}>
            <div className={css.relatedHead}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage id="LaunchPage.relatedLaunchesTitle" />
              </h2>
              <NamedLink className={css.allLaunchesLink} name="WhatsNewPage">
                <FormattedMessage id="LaunchPage.allLaunches" />
              </NamedLink>
            </div>
            <div className={css.launchRail}>
              {relatedLaunches.map(l => (
                <LaunchCard
                  key={l.id.uuid}
                  className={css.launchRailItem}
                  listing={l}
                  sourceSection="related-launches"
                  sizes="(max-width: 767px) 62vw, 260px"
                />
              ))}
            </div>
          </section>
        ) : null}
      </article>

      {/* ── sticky mobile action bar ─────────────────────────────────
          Hidden from viewportMedium up, where the row inside the article takes
          over. See the note on that row for why the pair can't drift.
          Always rendered: Share survives every status, including archived —
          where it is the ONLY action left, and gating the whole bar on
          save/sample would have taken it away on the phone. */}
      <div className={css.mobileBar}>
        {saveButton}
        {sampleButton}
        {showNotify ? (
          <a className={css.actionPrimary} href="#notify">
            <FormattedMessage id="LaunchPage.notifyCta" />
          </a>
        ) : null}
        <ShareButton
          className={css.actionShare}
          iconOnly
          title={title}
          text={trimmed(pd.subtitle) || undefined}
          onShare={method => fire(LAUNCH_EVENT_SHARE, { source_section: method })}
        />
      </div>

      {launchId ? (
        <LaunchSampleModal
          isOpen={sampleOpen}
          onClose={() => setSampleOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          launchId={launchId}
          launchTitle={title}
          brandName={brandLabel}
          currentUser={currentUser}
          onSubmitted={() => fire(LAUNCH_EVENT_SAMPLE_REQUEST)}
        />
      ) : null}

      <BrandRepModal
        isOpen={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        brandName={brandLabel}
        listingTitle={title}
        listingId={launchId}
        onSubmitInterest={payload => {
          fire(LAUNCH_EVENT_ENQUIRY);
          return submitBrandRepInterest(payload);
        }}
      />
    </div>
  );
};

export default SectionLaunchLayout;
